// src/context/AuthContext.js (VERSIÓN COMPLETA)
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile as updateAuthProfile
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';

export const AuthContext = createContext();

const ROLES = {
  ADMIN: 'admin',
  FEDERACION: 'federacion',
  ASOCIACION: 'asociacion',
  ORGANIZADOR: 'organizador',
  CRONOMETRADOR: 'cronometrador',
  JUEZ: 'juez',
  PRENSA: 'prensa',
  ENTRENADOR: 'entrenador',
  ATLETA: 'atleta',
};

const DEFAULT_ROLE = ROLES.ATLETA;

// Manejador de errores centralizado
const handleAuthError = (error) => {
  console.error("Error original en handleAuthError:", error);

  if (error.message === 'Verifica tu correo electrónico. Hemos enviado un nuevo enlace de verificación.') {
    return error.message;
  }

  const errorMap = {
    'auth/invalid-email': 'Correo electrónico no válido',
    'auth/user-disabled': 'Cuenta deshabilitada',
    'auth/user-not-found': 'Correo no registrado',
    'auth/wrong-password': 'Contraseña incorrecta',
    'auth/invalid-credential': 'Credenciales inválidas (correo o contraseña incorrectos)',
    'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
    'auth/email-already-in-use': 'El correo ya está registrado',
    'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
    'auth/account-exists-with-different-credential': 'Ya existe una cuenta con este correo usando otro método',
    'auth/popup-closed-by-user': 'El popup de autenticación fue cerrado',
    'auth/operation-not-allowed': 'Este método de autenticación no está habilitado',
    'auth/network-request-failed': 'Error de conexión. Verifica tu internet'
  };

  return errorMap[error.code] || error.message || 'Error en la autenticación. Intente nuevamente';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const cachedUser = localStorage.getItem('cachedUser');
    return cachedUser ? JSON.parse(cachedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const db = getFirestore();

  const handleAuthStateChange = useCallback(async (authUser) => {
    setLoading(true);
    
    try {
      if (authUser) {
        const userRef = doc(db, 'users', authUser.uid);
        let userData = null;
        
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          userData = userSnap.data();
        } else {
          userData = { 
            role: DEFAULT_ROLE,
            email: authUser.email,
            emailVerified: authUser.emailVerified,
            createdAt: new Date() 
          };
          await setDoc(userRef, userData);
        }

        // Normalizar el campo 'role' como 'rol' para consistencia
        const mergedUser = {
          uid: authUser.uid,
          email: authUser.email,
          emailVerified: authUser.emailVerified,
          displayName: authUser.displayName,
          photoURL: authUser.photoURL,
          ...userData,
          rol: userData.role || userData.rol || DEFAULT_ROLE
        };
        
        setUser(mergedUser);
        localStorage.setItem('cachedUser', JSON.stringify(mergedUser));
      } else {
        setUser(null);
        localStorage.removeItem('cachedUser');
        localStorage.removeItem('cachedUserData');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(handleAuthStateChange);
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [handleAuthStateChange]);
  
  const login = async (email, password) => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error("Error original en login:", error);
      throw new Error(handleAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
  
      const userCredential = await signInWithPopup(auth, provider);
      const userRef = doc(db, 'users', userCredential.user.uid);

      const userDataToSet = {
        email: userCredential.user.email,
        displayName: userCredential.user.displayName ?? null,
        photoURL: userCredential.user.photoURL ?? null,
        emailVerified: true,
      };

      await setDoc(userRef, userDataToSet, { merge: true });
      return userCredential.user;
    } catch (error) {
      console.error("Error completo en signInWithGoogle:", error);
      throw new Error(handleAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw new Error('Error al cerrar sesión: ' + error.message);
    }
  };

  // 🆕 Función para actualizar perfil
  const updateProfile = async (profileData) => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    try {
      setLoading(true);
      const userRef = doc(db, 'users', user.uid);
      
      // Preparar datos para actualizar
      const updateData = {
        ...profileData,
        updatedAt: new Date()
      };

      // Si se incluye displayName o photoURL, también actualizar en Firebase Auth
      if ((profileData.displayName && profileData.displayName !== user.displayName) || 
          (profileData.photoURL && profileData.photoURL !== user.photoURL)) {
        
        const authUpdateData = {};
        if (profileData.displayName) authUpdateData.displayName = profileData.displayName;
        if (profileData.photoURL) authUpdateData.photoURL = profileData.photoURL;
        
        await updateAuthProfile(auth.currentUser, authUpdateData);
      }

      // Actualizar en Firestore
      await updateDoc(userRef, updateData);

      // Actualizar estado local
      const updatedUser = { 
        ...user, 
        ...updateData,
        rol: updateData.role || user.rol // Mantener consistencia con rol
      };
      
      setUser(updatedUser);
      localStorage.setItem('cachedUser', JSON.stringify(updatedUser));

      return { success: true, message: 'Perfil actualizado correctamente' };
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      throw new Error(`Error al actualizar perfil: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Función para cambiar rol (útil para testing y administración)
  const updateUserRole = async (newRole) => {
    if (!user || !Object.values(ROLES).includes(newRole)) {
      throw new Error('Usuario no válido o rol no válido');
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { role: newRole });
      
      // Actualizar el usuario local
      const updatedUser = { ...user, rol: newRole, role: newRole };
      setUser(updatedUser);
      localStorage.setItem('cachedUser', JSON.stringify(updatedUser));
      
      console.log(`🔄 Rol actualizado a: ${newRole}`);
    } catch (error) {
      console.error('Error actualizando rol:', error);
      throw new Error('Error al actualizar el rol');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isOnline,
        ROLES,
        login,
        logout,
        signInWithGoogle,
        updateProfile,
        updateUserRole
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);